/**
 * Fase D (familia): los mensajes de buenos días de mamá, el huerto del balcón y el bizcocho chiffon.
 * Las frases marcadas como «práctica de inglés» se dejan en inglés a propósito en todos los idiomas.
 */
import type { Messages } from './en';

export const esFamily: Pick<Messages, 'family'> = {
  family: {
    mapNames: { balcony: 'Balcón' },
    zones: { balcony: 'Huerto del balcón' },
    objects: {
      fam_plot: 'Jardinera',
      fam_tap: 'Grifo',
    },
    npc: {},
    items: {
      tomato: { name: 'Tomate', desc: 'Jugoso y calentito del sol, de tu propio balcón. Cómetelo para ganar +20 de energía.' },
      sunflower: { name: 'Girasol', desc: 'Una flor grande y sonriente que has cultivado tú. Solo con mirarla, tu ánimo sube a 100.' },
      chiffon: { name: 'Bizcocho chiffon familiar', desc: 'Un 戚風蛋糕 alto y esponjoso hecho con tres fresas de tu huerto. Compártelo en casa o date un capricho.' },
    },

    // --- Mensajes de mamá -------------------------------------------------------------
    menuMom: '📩 Mensajes de mamá',
    toastNew: '📩 ¡Mamá te ha enviado un mensaje!',
    previewNone: 'Aún no hay mensajes',
    you: 'Tú',
    today: 'Hoy',
    sad: '😢',
    loveUp: (n: number) => `Mamá ❤️ +${n}`,
    loveDown: 'Mamá parece un poco triste…',
    ps: {
      water: 'P. D.: No te olvides de regar las plantas del balcón 🌱',
      ripe: 'P. D.: ¡Creo que en el balcón hay algo listo para recoger! 🍅🍓',
    },
    /**
     * Mensajes de mamá. `msgs` son suyos ("@card" = una imagen de buenos días); `replies` son
     * las opciones del jugador (práctica de inglés), cada una con las respuestas de mamá y un turno opcional.
     */
    texts: {
      rain: {
        icon: '☔',
        msgs: ['Buenos días, cariño ☔', 'Hoy va a llover. ¡Coge el paraguas! El azul está al lado del zapatero.'],
        replies: [
          {
            me: 'Got it, Mom! I’ll take the umbrella ☂️', tone: 'warm', love: 5,
            mom: ['Así me gusta 👍', 'Y nada de saltar en los charcos con los zapatos del cole. Que te conozco 😏'],
            next: [
              { me: 'Just ONE little puddle? 🥺', tone: 'warm', love: 3, mom: ['Jajaja. Vale, uno. Uno muy pequeñito.', '¡Y luego deja los zapatos secando junto a la puerta!'] },
              { me: 'I promise. No puddles!', tone: 'ok', mom: ['Me lo creeré cuando vea los calcetines secos 🧦😆'] },
            ],
          },
          {
            me: 'It’s only a little rain. I’ll be fine.', tone: 'ok',
            mom: ['¡En Taiwán, un poco de lluvia se convierte en un diluvio en cinco minutos! 🌧️', 'Llévatelo, porfa. ¿Por mí?'],
            next: [
              { me: 'Okay, okay, I’ll take it. For you ❤️', tone: 'warm', love: 4, mom: ['Gracias, cielo. Ahora puedo trabajar tranquila 😊'] },
              { me: 'Mom, you worry too much. Stop it.', tone: 'rude', mom: ['…Vale.', 'Las madres estamos hechas para preocuparnos. Llámame si te mojas.'] },
            ],
          },
          {
            me: 'Ugh, umbrellas are so annoying.', tone: 'rude',
            mom: ['Más rollo es ponerse malo…', 'Tu hermana pequeña se lleva el chubasquero. ¡Hasta ella me hace caso!'],
            next: [
              { me: 'Sorry, Mom. I’ll take it.', tone: 'warm', love: 3, mom: ['Gracias 🥰 Ese es mi niño.'] },
              { me: 'Fine… but it’s ugly.', tone: 'ok', mom: ['¡Pues cómprate uno bonito con tu paga! 😂'] },
            ],
          },
        ],
      },
      storm: {
        icon: '⛈️',
        msgs: ['⛈️ ¡Tormentas esta tarde!', 'El paraguas está en tu mochila. Si hay rayos, espera dentro. ¡Nada de ponerse debajo de los árboles!'],
        replies: [
          {
            me: 'I’ll wait inside until it stops. Promise! ⛈️', tone: 'warm', love: 6,
            mom: ['Qué listo es mi niño 🧠✨', 'Si se pone muy feo, llámame y voy a buscarte.'],
            next: [
              { me: 'Thanks, Mom. Can we have hot soup tonight? 🍲', tone: 'warm', love: 3, mom: ['Sopa de maíz, tu favorita. Trato hecho 😋'] },
              { me: 'Can Dad come instead? His car has music 🎶', tone: 'ok', mom: ['Hum. Mi moto también tiene música. ¡Canto yo! 🛵🎤😂'] },
            ],
          },
          {
            me: 'Thunder is cool! Can I watch the lightning?', tone: 'ok',
            mom: ['Desde la VENTANA, sí. Desde el patio, NO ⚡', 'Los rayos no son un vídeo, cariño 😅'],
            next: [
              { me: 'Haha, okay. From the window. Love you!', tone: 'warm', love: 4, mom: ['Yo te quiero más ❤️ ¡No te mojes!'] },
              { me: 'You never let me do anything fun.', tone: 'rude', mom: ['Te dejo hacer muchas cosas divertidas… solo que no las peligrosas.', 'Esta noche vemos la tormenta juntos, con algo para picar. ¿Vale?'] },
            ],
          },
          {
            me: 'I’m not a baby. Stop texting me.', tone: 'rude',
            mom: ['Vale. Es que te quiero, nada más.'],
            next: [{ me: 'Sorry, Mom… I love you too.', tone: 'warm', love: 4, mom: ['🥹 Vale. ¡Venga, vete, que llegas tarde!'] }],
          },
        ],
      },
      sunny: {
        icon: '☀️',
        msgs: ['¡Buenos días! ☀️ Hoy hace sol y calorcito.', '¡Bebe mucha agua! La botella está llena — no me la vuelvas a traer llena 😤'],
        replies: [
          {
            me: 'I’ll drink it ALL. Every drop! 💧', tone: 'warm', love: 5,
            mom: ['Jajaja. Esta noche reviso la botella 🕵️‍♀️'],
            next: [
              { me: 'Deal! And I’ll fill it again at school.', tone: 'warm', love: 3, mom: ['Hala. ¿Quién eres y qué has hecho con mi hijo? 😂❤️'] },
              { me: 'What do I get if it’s empty?', tone: 'ok', mom: ['Un abrazo. Y a lo mejor un té con perlas el domingo 🧋', '¡Con la mitad de azúcar!'] },
            ],
          },
          {
            me: 'But the school water tastes funny.', tone: 'ok',
            mom: ['¡Es agua y punto! Bueno… échale una rodaja de limón.', 'Hay limones en la nevera 🍋'],
            next: [
              { me: 'Lemon water! Good idea, thanks Mom 🍋', tone: 'warm', love: 4, mom: ['Estoy llena de buenas ideas. Díselo a tu padre 😎'] },
              { me: 'Lemon water is gross.', tone: 'rude', mom: ['La abuela lleva 80 años bebiendo agua con limón…', 'Vale, pues agua sola. ¡Pero BÉBETELA!'] },
            ],
          },
          {
            me: 'Water is boring. I want soda.', tone: 'rude',
            mom: ['Los refrescos llevan MUCHÍSIMO azúcar…', 'Tus dientes llorarán. Y luego lloraré yo.'],
            next: [{ me: 'Okay, water. For my teeth and for you 😁', tone: 'warm', love: 3, mom: ['¡Bien! Esta noche me enseñas esos dientes relucientes 😁🦷'] }],
          },
        ],
      },
      hot: {
        icon: '🥵',
        msgs: ['🥵 ¡Hoy vamos a llegar a 33 grados!', 'Ponte la gorra, bebe agua en cada recreo y quédate a la sombra a mediodía.'],
        replies: [
          {
            me: 'Hat on! I look super cool 🧢', tone: 'warm', love: 5,
            mom: ['El niño más guay de Taiwán 😎', '¡Y crema solar! Está en la estantería de la entrada.'],
            next: [
              { me: 'Got it! Sunscreen, hat, water ✅', tone: 'warm', love: 3, mom: ['¡Lista perfecta! La profe Mamá te pone un 100 💯'] },
              { me: 'Sunscreen is sticky…', tone: 'ok', mom: ['Mejor pegajoso que rojo como una gamba 🦐😂'] },
            ],
          },
          {
            me: 'Can I buy a shaved ice after school? 🍧', tone: 'ok',
            mom: ['Mmm… un granizado de mango pequeño.', '¡Pero primero vacía la botella de agua!'],
            next: [
              { me: 'Yes! Thank you, best Mom ever! 🥭', tone: 'warm', love: 4, mom: ['La mejor mamá del mundo también quiere un bocado 😋'] },
              { me: 'Can I get a BIG one?', tone: 'ok', mom: ['Pequeño. Tu barriguita es pequeña 😆'] },
            ],
          },
          {
            me: 'I hate hats. I’m not wearing it.', tone: 'rude',
            mom: ['Pues el sol te va a dar dolor de cabeza…', '¿Al menos quédate a la sombra, porfa?'],
            next: [{ me: 'Sorry. I’ll wear it, Mom.', tone: 'warm', love: 3, mom: ['Gracias, cariño. En agosto me lo agradecerás 😂'] }],
          },
        ],
      },
      cool: {
        icon: '🍂',
        msgs: ['Buenos días 🍂 Hoy refresca, solo 18 grados.', '¡Ponte la chaqueta! No llevarla en la mano — PONÉRTELA 🧥'],
        replies: [
          {
            me: 'Jacket is ON. Zipped up! 🧥', tone: 'warm', love: 5,
            mom: ['Qué mono y qué abrigadito 😍', 'También tienes una bebida calentita en el termo.'],
            next: [
              { me: 'Thanks, Mom! Is it ginger tea? 🫚', tone: 'warm', love: 3, mom: ['Té de jengibre con azúcar moreno. La receta de la abuela ❤️'] },
              { me: 'Do I have to drink all of it?', tone: 'ok', mom: ['Sí. Es mágico. Espanta los resfriados 🪄😆'] },
            ],
          },
          {
            me: '18 degrees isn’t cold!', tone: 'ok',
            mom: ['¡Para Taiwán es un FRÍO polar! 🥶😂', 'Todo el mundo en el cole irá con plumífero. Hazme caso.'],
            next: [
              { me: 'Haha, okay. I’ll wear it so you don’t worry.', tone: 'warm', love: 4, mom: ['Gracias, cielo. Madre contenta, madre callada 😌'] },
              { me: 'Down jackets are for old people.', tone: 'rude', mom: ['Yo llevo plumífero…', 'Vale, vale. ¿Al menos llévatelo, porfa?'] },
            ],
          },
          {
            me: 'I don’t need a jacket. Leave me alone.', tone: 'rude',
            mom: ['Cuando te resfríes, ¿quién hace la sopa? Yo.'],
            next: [{ me: 'Sorry, Mom… I’ll wear it.', tone: 'warm', love: 3, mom: ['Bien. Y haré sopa igualmente, por si acaso 🍲'] }],
          },
        ],
      },
      typhoon: {
        icon: '🌀',
        msgs: ['🌀 ¡Día de tifón! Ni cole ni trabajo: todos en casa.', '¿Me ayudas a prepararnos? Mete las plantas del balcón, llena botellas de agua y carga la linterna 🔦'],
        replies: [
          {
            me: 'Of course! I’ll help with everything 💪', tone: 'warm', love: 7,
            mom: ['Mi pequeño héroe 🦸', '¡Primero las plantas, antes de que sople fuerte!'],
            next: [
              { me: 'On it! Plants first, then the water bottles.', tone: 'warm', love: 3, mom: ['Menudo equipo. Luego fideos instantáneos y cartas 🍜🃏'] },
              { me: 'Can we watch the typhoon from the window?', tone: 'ok', mom: ['Lejos del cristal, sí. ¡Las ventanas se pueden romper!', 'Primero cinta en los cristales 🙂'] },
            ],
          },
          {
            me: 'A typhoon day! Can I play games all day? 🎮', tone: 'ok',
            mom: ['Jajaja. Todos los niños de Taiwán dicen lo mismo 😂', 'Primero ayudas y luego juegas. ¿Trato?'],
            next: [
              { me: 'Deal! Helping first 💪', tone: 'warm', love: 4, mom: ['¡Ese es mi niño! Te hago tus fideos favoritos 🍜'] },
              { me: 'No. It’s a holiday!', tone: 'rude', mom: ['No es fiesta para la gente que arregla los cables de la luz…', 'Seamos agradecidos y ayudemos un poco, ¿vale?'] },
            ],
          },
          {
            me: 'That’s boring. Ask my sister.', tone: 'rude',
            mom: ['Tu hermana tiene seis años. No puede con las macetas.', 'Hoy te necesito de verdad.'],
            next: [{ me: 'Sorry. I’ll help you, Mom.', tone: 'warm', love: 4, mom: ['Gracias, cariño. Juntos estaremos a salvo ❤️'] }],
          },
        ],
      },
      exam: {
        icon: '📝',
        msgs: ['¡Mucha suerte en el examen de hoy! 📝', 'Te he puesto un zongzi en la fiambrera. «Bāo zhòng» significa ¡que apruebas seguro! 😆'],
        replies: [
          {
            me: 'Thanks, Mom! I studied a lot. I’ll do my best! 📚', tone: 'warm', love: 6,
            mom: ['Lo sé. Anoche vi tu luz encendida 💡', 'Saques lo que saques, estoy orgullosa de ti.'],
            next: [
              { me: 'That means a lot. Love you ❤️', tone: 'warm', love: 3, mom: ['❤️❤️❤️ ¡Ahora ve y enséñale a ese examen quién manda!'] },
              { me: 'Even if I get a 60?', tone: 'ok', mom: ['Aunque saques un 60. Pero un 61 es mejor 😂', '¡Lee cada pregunta dos veces!'] },
            ],
          },
          {
            me: 'I’m so nervous…', tone: 'ok',
            mom: ['Respira hondo tres veces. Coge aire… y suéltalo… 🌬️', 'Estar nervioso significa que te importa. Eso es bueno.'],
            next: [
              { me: 'Okay… breathing. Thanks, Mom. I feel better.', tone: 'warm', love: 5, mom: ['¡Tú puedes! Te animo desde el trabajo 📣'] },
              { me: 'What if I forget everything?', tone: 'ok', mom: ['No se te olvidará. Y si pasa, es un examen, no toda tu vida 🤗'] },
            ],
          },
          {
            me: 'Don’t remind me! You’re making it worse!', tone: 'rude',
            mom: ['Perdona… solo quería darte ánimos.'],
            next: [{ me: 'Sorry, Mom. I’m just stressed. Thank you.', tone: 'warm', love: 4, mom: ['Lo sé, cariño. Un abrazo enorme 🤗 ¡Poder zongzi!'] }],
          },
        ],
      },
      dinner: {
        icon: '🍗',
        msgs: ['¡Buenos días! ¿A que no sabes qué hay para cenar? 😋', '¡Pollo de las tres tazas! 三杯雞 — con mucha albahaca 🌿'],
        replies: [
          {
            me: 'YES! My favorite! Can I help you cook? 🍗', tone: 'warm', love: 6,
            mom: ['¡Claro! Tú coges la albahaca y remueves la olla.', '¡A las seis en casa, chef! 👩‍🍳'],
            next: [
              { me: 'Chef reporting for duty at six! 🫡', tone: 'warm', love: 3, mom: ['¡Jajaja, chef! Te busco un delantal 😆'] },
              { me: 'Can I just do the tasting part? 😋', tone: 'ok', mom: ['El que prueba, friega los platos 😏'] },
            ],
          },
          {
            me: 'What are the three cups?', tone: 'ok',
            mom: ['Aceite de sésamo, vino de arroz y salsa de soja: ¡una taza de cada! 🍶', 'Ya conoces un secreto de familia 🤫'],
            next: [
              { me: 'Cool! Will you teach me the recipe someday?', tone: 'warm', love: 4, mom: ['¿Algún día? ¡Este fin de semana! 🥰'] },
              { me: 'Is there a cup of chocolate? 🍫', tone: 'ok', mom: ['¡Jajaja, no! Eso es el postre, tontorrón 😂'] },
            ],
          },
          {
            me: 'Chicken again? Boring.', tone: 'rude',
            mom: ['Me levanto temprano para comprar el pollo en el mercado…'],
            next: [
              { me: 'Sorry, Mom. I love your three-cup chicken. Really!', tone: 'warm', love: 4, mom: ['Hum… vale. Te toca el trozo más grande 😌'] },
              { me: 'Can we have pizza instead?', tone: 'ok', mom: ['Pizza el sábado. Hoy, pollo de las tres tazas. Normas de mamá 📏'] },
            ],
          },
        ],
      },
      card: {
        icon: '🌻',
        card: { theme: 1, big: 'Buenos días', sub: 'Paz y salud cada día' },
        msgs: ['@card', 'Buenos días, mi vida 🌸🌸 ¡Paz y salud cada día! 🙏'],
        replies: [
          {
            me: 'Good morning, Mom! Pretty flowers 🌻', tone: 'warm', love: 5,
            mom: ['¿A que sí? Me la mandó primero la tía Lin 😆', 'Se la he reenviado a cuarenta personas.'],
            next: [
              { me: 'Forty?! You’re the queen of good-morning pictures 👑', tone: 'warm', love: 3, mom: ['Gracias, gracias 👸 Tengo 300 más guardadas 😂'] },
              { me: 'Mom… you don’t have to send one every day 😅', tone: 'ok', mom: ['¿Y entonces cómo sabrás que estoy bien? 😂', '¡Una imagen al día = mamá está bien!'] },
            ],
          },
          {
            me: '@card', tone: 'warm', love: 5,
            mom: ['¡HALA! ¡Mi hijo ME ha mandado una imagen! 🥹', 'Se la voy a enseñar a todo el trabajo.'],
            next: [
              { me: 'Please don’t show everyone 😳', tone: 'ok', mom: ['Demasiado tarde. La señora Chen dice que eres un encanto 😆'] },
              { me: 'Tell them I said hi! 👋', tone: 'warm', love: 2, mom: ['¡Todos te devuelven el saludo! Eres famoso 🌟'] },
            ],
          },
          {
            me: 'Not another one of those pictures 🙄', tone: 'rude',
            mom: ['Tardé tres minutos en elegir esa…'],
            next: [{ me: 'Sorry, Mom. It IS pretty 🌸', tone: 'warm', love: 3, mom: ['Jeje. Mañana elijo una con un gato 🐱'] }],
          },
        ],
      },
    },
    /** Texto de la respuesta «enviar una imagen de vuelta». */
    sendCard: 'Enviarle una imagen de buenos días 🖼️',
    replyCard: { theme: 4, big: 'Buenos días', sub: 'Te quiero, mamá' },

    // --- Huerto del balcón --------------------------------------------------------------
    crops: {
      strawberry: { name: 'Fresa', icon: '🍓', seed: 'Semillas de fresa' },
      tomato: { name: 'Tomate', icon: '🍅', seed: 'Semillas de tomate' },
      sunflower: { name: 'Girasol', icon: '🌻', seed: 'Semillas de girasol' },
    },
    stages: ['Tierra vacía', 'Plántula', 'Brote', 'Creciendo', '¡Madura!'],
    plotTitle: (n: number) => `Jardinera ${n}`,
    emptyText: 'Soft, dark soil. What do you want to plant?', // práctica de inglés
    plant: (seed: string) => `🌱 Plantar ${seed}`,
    free: 'gratis',
    notNow: 'Ahora no',
    noCoins: 'No tienes monedas suficientes para esas semillas.',
    planted: (crop: string) => `You planted ${crop}! Water it once a day.`, // práctica de inglés
    watered: (n: number) => `💧 ¡Regado! (${n}/3)`,
    rainWatered: '🌧️ Hoy la lluvia ha regado tus plantas.',
    alreadyWatered: 'La tierra aún está húmeda. ¡Vuelve mañana!',
    canEmpty: 'La regadera está vacía: llénala en el grifo.',
    harvest: (what: string) => `Harvest time! You picked ${what}.`, // práctica de inglés
    growText: (crop: string, stage: string, n: number) => `${crop} · ${stage} · regado ${n}/3 días`,
    covered: 'Las plantas están a salvo bajo la lona hasta que pase el tifón.',
    tapTitle: 'Grifo',
    tapText: 'Un grifo viejo y oxidado con un cubo azul. La regadera está al lado.',
    fillCan: (n: number) => `🪣 Llenar la regadera (${n} riegos)`,
    canFilled: 'Glu, glu… ¡la regadera está llena!',
    fillBottle: '💧 Llenar tu botella de agua',
    bottleFilled: 'Tu botella vuelve a estar llena.',
    canState: (n: number) => `Regadera: quedan ${n}`,
    plantsSafe: '🪴 Las plantas del balcón están a salvo dentro. ¡Bien pensado!',
    typhoonWarn: '🌀 ¡Hoy hay tifón! Mete las plantas del balcón antes de la noche.',
    typhoonHit: '🌀 El tifón de anoche ha aplastado las plantas del balcón… tendrán que volver a crecer.',
    tomatoEat: '¡Dulce y jugoso! +20 de energía.',
    sunflowerLook: 'El girasol te sonríe. ¡Te sientes de maravilla!',

    // --- Horno: bizcocho chiffon --------------------------------------------------------
    chiffon: '🎂 Hornear un bizcocho chiffon familiar 戚風蛋糕',
    chiffonNeeds: 'Un bizcocho chiffon necesita 3 fresas. ¡Cultívalas en el balcón!',
    chiffonWork: 'Separando las yemas, montando el merengue, incorporando las fresas…',
    chiffonDone: '¡Alto y esponjoso como una nube! ¡Un 戚風蛋糕 familiar!',
    chiffonShare: 'Todos se reúnen alrededor. «¡Qué blandito! ¡Como una nube!» Toda la familia sonríe.',
    chiffonEat: 'Te comes un buen trozo esponjoso. ¡Felicidad pura!',

    journal: {
      harvest: 'Has recogido {arg} del huerto del balcón',
      chiffon: 'Has horneado un bizcocho chiffon familiar con fresas de tu huerto',
    },
  },
};
